import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CalendarPlusIcon,
  ClockIcon,
  MapPinIcon,
  MessageCircleIcon,
  MicIcon,
  PencilIcon,
  PlusIcon,
  Share2Icon,
  Trash2Icon,
  UsersIcon,
  VideoIcon,
  XIcon,
} from 'lucide-react';
import { AGENDA_DESCRIPTION_MAX, TeamsDeeplink } from '@kennisbank/shared';
import type {
  AgendaEvent,
  AgendaEventType,
  AgendaOrganisator,
  AgendaRegistration,
  AgendaSpreker,
  CreateAgendaEventInput,
  IntervisieGroup,
  SmoelenboekEntry,
  UpdateAgendaEventInput,
} from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { AgendaRegistrations } from '../components/AgendaRegistrations.js';
import { RichText } from '../components/RichText.js';
import { RichTextEditor } from '../components/RichTextEditor.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useOnbewaardeWijzigingen } from '../hooks/useOnbewaardeWijzigingen.js';
import { ApiError, apiDelete, apiGet, apiPost, apiPut } from '../api/client.js';
import { downloadIcs, mapsUrl } from '../lib/ics.js';
import { teamsHulptekst } from '../lib/teams.js';
import { useTenantBrand } from '../lib/tenant-brand.js';
import { MONTH_ABBR, MONTH_SHORT, isUpcoming, langeDatum, parseDate } from '../lib/datum.js';

/**
 * NL-labels bij de gecureerde enum uit shared. De volgorde is de volgorde in de
 * typekiezer; `overig` staat achteraan omdat het de vangnetwaarde is.
 */
const TYPE_LABEL: Record<AgendaEventType, string> = {
  nascholing: 'Nascholing',
  teambuilding: 'Teambuilding',
  intervisie: 'Intervisie',
  overig: 'Overig',
};
const TYPE_VOLGORDE: AgendaEventType[] = ['nascholing', 'teambuilding', 'intervisie', 'overig'];

/**
 * Zelfde pil-vorm als de categoriechip in Nieuws (rond, zachte 50-tint, geen
 * kapitalen): binnen de app is dát het bestaande categoriepatroon, en twee
 * verschillende chipvormen naast elkaar oogt rommelig. Kleur volgt DESIGN.md:
 * mosterdgeel voor het eigen scholingsaanbod, petrol voor teambuilding, aqua
 * voor intervisie en neutraal antraciet voor overig.
 */
const TYPE_CHIP: Record<AgendaEventType, string> = {
  // ring1-fg en niet mosterdgeel-600: die laatste haalt op deze achtergrond maar
  // 3,7:1 en zakt daarmee onder de leesbaarheidsnorm voor kleine tekst.
  nascholing: 'bg-ring1-bg text-ring1-fg',
  teambuilding: 'bg-petrol-50 text-petrol-700',
  intervisie: 'bg-brand-50 text-brand-700',
  overig: 'bg-ink-50 text-ink-500',
};

/**
 * `groep` staat alleen op intervisiesessies. Die staan sinds 17 sep 2026 tussen
 * de gewone agenda-items van de leden, en zonder de groepsnaam is niet te zien
 * bij welke van je groepen zo'n sessie hoort. Eén chip in plaats van twee: twee
 * pillen naast elkaar lezen als twee losse categorieën.
 */
function TypeChip({
  type,
  groep,
  className,
}: {
  type: AgendaEventType;
  groep?: string;
  className?: string;
}): JSX.Element {
  return (
    <span
      className={`inline-flex max-w-full shrink-0 items-center truncate rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${TYPE_CHIP[type]} ${className ?? ''}`}
    >
      {groep ? `${TYPE_LABEL[type]} · ${groep}` : TYPE_LABEL[type]}
    </span>
  );
}

/** Dezelfde validator als de backend (shared) — geen tweede regel in de UI. */
function isTeamsUrl(raw: string): boolean {
  return TeamsDeeplink.safeParse(raw).success;
}

// ── Form modal (create + edit) ──────────────────────────────────────────────

interface FormState {
  title: string;
  date: string; // YYYY-MM-DD
  time: string;
  location: string;
  address: string;
  description: string;
  evenementtype: AgendaEventType;
  teamsUrl: string;
  intervisieGroupId: string;
  registrationsOpen: boolean;
  organisatoren: AgendaOrganisator[];
  sprekers: AgendaSpreker[];
}

function emptyForm(): FormState {
  return {
    title: '',
    date: '',
    time: '',
    location: '',
    address: '',
    description: '',
    evenementtype: 'overig',
    teamsUrl: '',
    intervisieGroupId: '',
    registrationsOpen: false,
    organisatoren: [],
    sprekers: [],
  };
}

function eventToForm(event: AgendaEvent): FormState {
  return {
    title: event.title,
    date: event.date,
    time: event.time,
    location: event.location ?? '',
    address: event.address ?? '',
    description: event.description ?? '',
    evenementtype: event.evenementtype,
    // Is `teamsUrl` de terugval-link van de groep, dan hoort hij niet in het
    // veld: opslaan zou hem als eigen link bevriezen en een latere wijziging op
    // de groep zou dit item dan niet meer bereiken.
    teamsUrl: event.teamsUrlVanGroep ? '' : (event.teamsUrl ?? ''),
    intervisieGroupId: event.intervisieGroupId ?? '',
    registrationsOpen: event.registrationsOpen ?? false,
    organisatoren: event.organisatoren ?? [],
    sprekers: event.sprekers ?? [],
  };
}

function EventFormModal({
  initial,
  isAdmin,
  onSave,
  onClose,
  saving,
  saveError,
}: {
  initial: AgendaEvent | null;
  /** Alleen een beheerder mag het type `intervisie` kiezen (backend dwingt af). */
  isAdmin: boolean;
  onSave: (input: CreateAgendaEventInput) => void;
  onClose: () => void;
  saving: boolean;
  saveError: string;
}): JSX.Element {
  const [beginstand] = useState<FormState>(() => (initial ? eventToForm(initial) : emptyForm()));
  const [form, setForm] = useState<FormState>(beginstand);
  const [error, setError] = useState('');
  const { bevestigSluiten } = useOnbewaardeWijzigingen(
    JSON.stringify(form) !== JSON.stringify(beginstand),
  );
  function sluitMetBevestiging(): void {
    if (bevestigSluiten()) onClose();
  }
  const [groups, setGroups] = useState<IntervisieGroup[] | null>(null);
  const [groupsError, setGroupsError] = useState('');

  const isIntervisie = form.evenementtype === 'intervisie';

  // Groepen komen uit Beheer Intervisie en zijn beheerder-only, net als het
  // type zelf. Pas ophalen als iemand het type kiest — de meeste items zijn
  // geen intervisiesessie.
  useEffect(() => {
    if (!isIntervisie || !isAdmin || groups !== null) return;
    void apiGet<{ groups: IntervisieGroup[] }>('/api/v1/beheer/intervisie')
      .then((d) => setGroups(d.groups))
      .catch(() => setGroupsError('Kon de intervisiegroepen niet laden.'));
  }, [isIntervisie, isAdmin, groups]);

  function set(
    field: keyof FormState,
    value: string | boolean | AgendaOrganisator[] | AgendaSpreker[],
  ): void {
    setForm((f) => ({ ...f, [field]: value }));
  }

  // Collega's voor de organisator- en sprekerskeuze; het Smoelenboek-endpoint bestaat al.
  const [collegas, setCollegas] = useState<SmoelenboekEntry[] | null>(null);
  useEffect(() => {
    void apiGet<{ users: SmoelenboekEntry[] }>('/api/v1/users/smoelenboek')
      .then((d) =>
        setCollegas([...d.users].sort((a, b) => a.displayName.localeCompare(b.displayName, 'nl'))),
      )
      .catch(() => setCollegas([]));
  }, []);
  const [externeSpreker, setExterneSpreker] = useState('');
  function voegExterneSprekerToe(): void {
    const naam = externeSpreker.trim();
    if (!naam) return;
    if (!form.sprekers.some((x) => !x.userId && x.naam === naam)) {
      set('sprekers', [...form.sprekers, { naam }]);
    }
    setExterneSpreker('');
  }

  function handleSave(): void {
    if (!form.title.trim()) {
      setError('Vul een titel in.');
      return;
    }
    if (!form.date) {
      setError('Kies een datum.');
      return;
    }
    if (!form.time.trim()) {
      setError('Vul een tijd in.');
      return;
    }
    const teamsUrl = form.teamsUrl.trim();
    if (teamsUrl && !isTeamsUrl(teamsUrl)) {
      setError(
        'De Teams-link klopt niet. Plak de volledige deelnamelink uit Teams; die begint met https://teams.microsoft.com/.',
      );
      return;
    }
    if (isIntervisie && !form.intervisieGroupId) {
      setError('Kies een intervisiegroep bij het type Intervisie.');
      return;
    }
    // Dezelfde grens als het schema, maar met een leesbare melding: de server
    // antwoordt met "Request payload failed validation" en daar kun je niets mee.
    if (form.description.length > AGENDA_DESCRIPTION_MAX) {
      setError(
        'De beschrijving is te lang om op te slaan. Kort hem in of haal een geplakte afbeelding eruit; je tekst blijft staan zolang je dit venster openhoudt.',
      );
      return;
    }
    setError('');
    // Bij bewerken moet een leeggemaakt veld als '' meegaan: dat is het signaal
    // "wissen". Zou het wegvallen, dan laat de backend de oude waarde staan en
    // krijg je een link die niet meer weg te halen is. Bij aanmaken valt het weg.
    const leeg = initial !== null ? '' : undefined;
    onSave({
      title: form.title.trim(),
      date: form.date,
      time: form.time.trim(),
      location: form.location.trim() || leeg,
      address: form.address.trim() || leeg,
      description: form.description.trim() || leeg,
      evenementtype: form.evenementtype,
      // Leeg = geen eigen link; bij intervisie geldt dan die van de groep.
      teamsUrl: teamsUrl || leeg,
      intervisieGroupId: isIntervisie ? form.intervisieGroupId : leeg,
      registrationsOpen: form.registrationsOpen,
      organisatoren: form.organisatoren,
      sprekers: form.sprekers,
    });
  }

  const isEditing = initial !== null;
  // Een bestaand intervisie-item blijft kiesbaar voor de beheerder die het
  // opent; kantoorpersoneel komt hier niet, want de knoppen zijn dan verborgen.
  const typeKeuzes = TYPE_VOLGORDE.filter(
    (t) => t !== 'intervisie' || isAdmin || initial?.evenementtype === 'intervisie',
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) sluitMetBevestiging();
      }}
    >
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col gap-5 overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-[16px] font-bold text-ink-900">
          {isEditing ? 'Evenement aanpassen' : 'Evenement toevoegen'}
        </h2>

        <div className="flex flex-col gap-3">
          <Field label="Titel">
            <input
              type="text"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="Naam van het evenement"
              className={INPUT_CLS}
            />
          </Field>

          <Field label="Type">
            <div className="flex flex-wrap gap-1.5">
              {typeKeuzes.map((t) => (
                <button
                  key={t}
                  type="button"
                  aria-pressed={form.evenementtype === t}
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      evenementtype: t,
                      // De groep hoort alleen bij intervisie; de link mag bij elk type.
                      intervisieGroupId: t === 'intervisie' ? f.intervisieGroupId : '',
                    }))
                  }
                  className={
                    form.evenementtype === t
                      ? 'rounded-lg border border-brand-600 bg-brand-600 px-3 py-1.5 text-[12px] font-semibold text-white transition-colors'
                      : 'rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-1.5 text-[12px] font-medium text-ink-900/70 hover:bg-brand-50 transition-colors'
                  }
                >
                  {TYPE_LABEL[t]}
                </button>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Datum">
              <input
                type="date"
                value={form.date}
                onChange={(e) => set('date', e.target.value)}
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Tijd">
              <input
                type="text"
                value={form.time}
                onChange={(e) => set('time', e.target.value)}
                placeholder="bijv. 09:30 - 16:00"
                className={INPUT_CLS}
              />
            </Field>
          </div>

          <Field label="Locatie" optional>
            <input
              type="text"
              value={form.location}
              onChange={(e) => set('location', e.target.value)}
              placeholder="bijv. De VerbroederIJ"
              className={INPUT_CLS}
            />
          </Field>

          <Field label="Adres" optional>
            <input
              type="text"
              value={form.address}
              onChange={(e) => set('address', e.target.value)}
              placeholder="bijv. Johan van Hasseltweg 21, 1021 KN Amsterdam"
              className={INPUT_CLS}
            />
          </Field>

          {isIntervisie ? (
            <Field label="Intervisiegroep">
              <select
                value={form.intervisieGroupId}
                onChange={(e) => set('intervisieGroupId', e.target.value)}
                className={INPUT_CLS}
              >
                <option value="">{groups === null ? 'Laden…' : 'Kies een groep…'}</option>
                {(groups ?? []).map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
              <p className="mt-1.5 text-[11px] text-ink-900/45">
                Alleen leden van deze groep zien de sessie.
              </p>
              {groupsError && <p className="mt-1.5 text-[11px] text-error">{groupsError}</p>}
              {groups !== null && groups.length === 0 && (
                <p className="mt-1.5 text-[11px] text-error">
                  Er zijn nog geen intervisiegroepen. Maak er eerst een aan bij Beheer Intervisie.
                </p>
              )}
            </Field>
          ) : null}
          <Field label="Teams-link" optional>
            <input
              type="url"
              value={form.teamsUrl}
              onChange={(e) => set('teamsUrl', e.target.value)}
              placeholder="https://teams.microsoft.com/l/meetup-join/…"
              className={INPUT_CLS}
            />
            {form.teamsUrl.trim() && !isTeamsUrl(form.teamsUrl.trim()) ? (
              <p className="mt-1.5 text-[11px] text-error">
                Dit is geen geldige Teams-link. Plak de volledige deelnamelink uit de
                Teams-vergadering; die begint met https://teams.microsoft.com/.
              </p>
            ) : (
              <p className="mt-1.5 text-[11px] text-ink-900/45">
                {isIntervisie
                  ? 'De deelnamelink van deze bijeenkomst. Leeg laten = de link van de groep.'
                  : 'Plak de deelnamelink uit de Teams-vergadering. Iedereen die dit evenement ziet, kan meedoen.'}
              </p>
            )}
          </Field>

          <Field label="Beschrijving" optional>
            <RichTextEditor
              value={form.description}
              onChange={(html) => set('description', html)}
              placeholder="Meer informatie over dit evenement…"
            />
            <p className="mt-1.5 text-[11px] text-ink-900/45">
              Selecteer tekst voor vet, cursief, lijsten en links.
            </p>
          </Field>

          <Field label="Organisatoren" optional>
            <CollegaKiezer
              collegas={collegas}
              gekozen={form.organisatoren.map((o) => ({ userId: o.userId, naam: o.displayName }))}
              rol="organisator"
              onToggle={(c) =>
                set(
                  'organisatoren',
                  form.organisatoren.some((x) => x.userId === c.userId)
                    ? form.organisatoren.filter((x) => x.userId !== c.userId)
                    : [...form.organisatoren, { userId: c.userId, displayName: c.naam }],
                )
              }
            />
            <p className="mt-1.5 text-[11px] text-ink-900/45">
              Een organisator ziet de aanmeldingen van dit evenement, ook zonder beheerrechten, en
              krijgt een melding bij elke nieuwe aanmelding.
            </p>
          </Field>

          <Field label="Sprekers of trainers" optional>
            <CollegaKiezer
              collegas={collegas}
              gekozen={form.sprekers.map((s) => ({ userId: s.userId, naam: s.naam }))}
              rol="spreker"
              onToggle={(c) =>
                set(
                  'sprekers',
                  form.sprekers.some((x) => x.userId === c.userId)
                    ? form.sprekers.filter((x) => x.userId !== c.userId)
                    : [...form.sprekers, { naam: c.naam, userId: c.userId }],
                )
              }
              onVerwijderExtern={(naam) =>
                set(
                  'sprekers',
                  form.sprekers.filter((x) => x.userId || x.naam !== naam),
                )
              }
            />
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={externeSpreker}
                onChange={(e) => setExterneSpreker(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    voegExterneSprekerToe();
                  }
                }}
                maxLength={200}
                placeholder="Externe trainer (naam)…"
                aria-label="Naam van een externe spreker of trainer"
                className={INPUT_CLS}
              />
              <button
                type="button"
                onClick={voegExterneSprekerToe}
                disabled={!externeSpreker.trim()}
                className="shrink-0 rounded-lg border border-brand-100 bg-white px-3 py-2 text-[13px] font-medium text-ink-900/70 hover:bg-brand-50 disabled:opacity-50"
              >
                Toevoegen
              </button>
            </div>
            <p className="mt-1.5 text-[11px] text-ink-900/45">
              Collega&apos;s krijgen een link naar het Smoelenboek; een externe trainer staat alleen
              met naam.
            </p>
          </Field>

          <label className="flex cursor-pointer items-start gap-2.5 rounded-lg bg-brand-50/50 p-3">
            <input
              type="checkbox"
              checked={form.registrationsOpen}
              onChange={(e) => set('registrationsOpen', e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600"
            />
            <span className="text-[12px] text-ink-900/70">
              <span className="font-medium text-ink-900">Aanmelden mogelijk</span>
              <br />
              Toont een aanmeldknop bij het evenement. De deelnemerslijst is zichtbaar voor
              kantoorpersoneel en beheerders, bij het evenement en in het tabblad Aanmeldingen.
            </span>
          </label>
        </div>

        {(error || saveError) && <p className="text-[12px] text-error">{error || saveError}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={sluitMetBevestiging}
            className="rounded-xl px-4 py-2 text-[13px] text-ink-900/60 hover:bg-brand-50 transition-colors"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-xl bg-brand-600 px-5 py-2 text-[13px] font-semibold text-white hover:bg-brand-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Opslaan…' : isEditing ? 'Opslaan' : 'Toevoegen'}
          </button>
        </div>
      </div>
    </div>
  );
}

const INPUT_CLS =
  'w-full rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400';

/**
 * Zelfde kiezer als de leden in Beheer Intervisie: gekozen personen als chips
 * bovenaan (alleen het kruisje verwijdert), daaronder zoeken en aanvinken.
 * Een gekozene zonder userId is een externe naam en verschijnt alleen als chip.
 */
function CollegaKiezer({
  collegas,
  gekozen,
  rol,
  onToggle,
  onVerwijderExtern,
}: {
  collegas: SmoelenboekEntry[] | null;
  gekozen: Array<{ userId?: string; naam: string }>;
  rol: 'organisator' | 'spreker';
  onToggle: (c: { userId: string; naam: string }) => void;
  onVerwijderExtern?: (naam: string) => void;
}): JSX.Element {
  const [zoek, setZoek] = useState('');
  const q = zoek.trim().toLowerCase();
  const lijst = (collegas ?? []).filter(
    (u) => !q || u.displayName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
  );
  return (
    <>
      {gekozen.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-1.5">
          {gekozen.map((g) => (
            <li
              key={g.userId ?? `extern-${g.naam}`}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[12px] font-medium ring-1 ${
                g.userId
                  ? 'bg-white text-ink-900 ring-brand-100'
                  : 'bg-brand-50 text-ink-900 ring-brand-100'
              }`}
            >
              {g.naam}
              {!g.userId && <span className="text-ink-900/50">(extern)</span>}
              <button
                type="button"
                onClick={() =>
                  g.userId
                    ? onToggle({ userId: g.userId, naam: g.naam })
                    : onVerwijderExtern?.(g.naam)
                }
                aria-label={`Verwijder ${g.naam} als ${rol}`}
                className="rounded-full p-0.5 text-ink-900/60 hover:bg-error/10 hover:text-error"
              >
                <XIcon size={12} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        type="text"
        value={zoek}
        onChange={(e) => setZoek(e.target.value)}
        placeholder="Zoek op naam of e-mail…"
        aria-label={`Zoek een collega om als ${rol} toe te voegen`}
        className={INPUT_CLS}
      />
      <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-ring2">
        {collegas === null ? (
          <p className="px-3 py-4 text-center text-[13px] text-ink-900/50">Laden…</p>
        ) : lijst.length === 0 ? (
          <p className="px-3 py-4 text-center text-[13px] text-ink-900/50">
            Geen collega&apos;s gevonden.
          </p>
        ) : (
          lijst.map((c) => (
            <label
              key={c.id}
              className="flex cursor-pointer items-center gap-2.5 border-b border-brand-50 px-3 py-2 text-sm last:border-b-0 hover:bg-brand-50/50"
            >
              <input
                type="checkbox"
                checked={gekozen.some((g) => g.userId === c.id)}
                onChange={() => onToggle({ userId: c.id, naam: c.displayName })}
                className="h-4 w-4 accent-brand-500"
              />
              <span className="min-w-0 flex-1 truncate text-ink-900">
                {c.displayName}
                {c.specialisaties && c.specialisaties.length > 0 && (
                  <span className="block truncate text-[11px] text-ink-900/70">
                    {c.specialisaties.join(' · ')}
                  </span>
                )}
              </span>
            </label>
          ))
        )}
      </div>
    </>
  );
}

function Field({
  label,
  optional,
  children,
}: {
  label: string;
  optional?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <label className="mb-1 block text-[12px] font-medium text-ink-900/70">
        {label} {optional && <span className="text-ink-900/35">(optioneel)</span>}
      </label>
      {children}
    </div>
  );
}

// ── Aanmeldingenoverzicht (kantoorpersoneel) ────────────────────────────────

interface OverzichtRij {
  event: AgendaEvent;
  registrations: AgendaRegistration[];
}

/** Excel opent CSV standaard met puntkomma in de NL-locale. */
export function naarCsv(rij: OverzichtRij): string {
  const veld = (v: string | undefined): string => {
    const waarde = v ?? '';
    // Excel evalueert een cel ná het uitpakken van de quotes: een opmerking of
    // zelfgekozen weergavenaam die met = + - @ begint zou dus als formule
    // draaien en het hele blad (e-mails, lidnummers) kunnen wegsluizen. Een
    // apostrof ervoor maakt er tekst van.
    const veilig = /^[=+\-@\t\r]/.test(waarde) ? `'${waarde}` : waarde;
    return `"${veilig.replaceAll('"', '""')}"`;
  };
  const regels = [
    ['Naam', 'E-mail', 'LVPOH', 'SKJ', 'Opmerking', 'Aangemeld op'].join(';'),
    // Zelfde volgorde als de tabel erboven; anders lijkt de export een andere lijst.
    ...[...rij.registrations]
      .sort((a, b) => a.displayName.localeCompare(b.displayName, 'nl'))
      .map((r) =>
        [
          veld(r.displayName),
          veld(r.email),
          veld(r.lidnummerLvpoh),
          veld(r.lidnummerSkj),
          veld(r.note),
          veld(nlDatum(r.createdAt)),
        ].join(';'),
      ),
  ];
  return regels.join('\r\n');
}

/**
 * De aanmeldtijd staat als UTC-ISO opgeslagen; een aanmelding om 00:30 NL-tijd
 * zou anders op de dag ervóór in de export komen.
 */
function nlDatum(iso: string): string {
  return new Date(iso).toLocaleDateString('nl-NL', { timeZone: 'Europe/Amsterdam' });
}

function downloadCsv(rij: OverzichtRij): void {
  // BOM: zonder dit leest Excel de accenten in Nederlandse namen verkeerd.
  const blob = new Blob([`\ufeff${naarCsv(rij)}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `aanmeldingen-${rij.event.date}-${rij.event.title.replace(/[^\w-]+/g, '-')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function OverzichtKaart({ rij }: { rij: OverzichtRij }): JSX.Element {
  const { weekday, day, month } = parseDate(rij.event.date);
  return (
    <div className="rounded-2xl border border-brand-100 bg-white p-5 shadow-sm">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="mb-1 flex items-center gap-2">
            <h3 className="text-[15px] font-semibold text-ink-900">{rij.event.title}</h3>
            <TypeChip type={rij.event.evenementtype} groep={rij.event.intervisieGroupNaam} />
          </div>
          <p className="text-[12px] text-ink-900/55">
            {weekday} {day} {MONTH_SHORT[month]} · {rij.event.time}
            {rij.event.location && ` · ${rij.event.location}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-brand-50 px-2.5 py-0.5 text-[11px] font-semibold text-brand-700">
            {rij.registrations.length}{' '}
            {rij.registrations.length === 1 ? 'aanmelding' : 'aanmeldingen'}
          </span>
          {rij.registrations.length > 0 && (
            <button
              type="button"
              onClick={() => downloadCsv(rij)}
              className="rounded-lg border border-brand-200 px-3 py-1.5 text-[12px] font-medium text-brand-700 hover:bg-brand-50 transition-colors"
            >
              Download CSV
            </button>
          )}
        </div>
      </div>

      {rij.registrations.length === 0 ? (
        <p className="text-[12px] text-ink-900/45">Nog niemand aangemeld.</p>
      ) : (
        <div className="overflow-x-auto">
          {/* min-w: zonder dit persen vier kolommen zich op een telefoon samen
              in plaats van te scrollen. */}
          <table className="w-full min-w-[560px] text-left text-[13px]">
            <thead>
              <tr className="border-b border-brand-100 text-[12px] uppercase tracking-wide text-ink-900/70">
                <th className="py-2 pr-4 font-medium">Naam</th>
                <th className="py-2 pr-4 font-medium">E-mail</th>
                <th className="py-2 pr-4 font-medium">Lidnummers</th>
                <th className="py-2 font-medium">Opmerking</th>
              </tr>
            </thead>
            <tbody>
              {/* Op naam, niet op aanmeldvolgorde: bij dertig deelnemers is dat
                  het verschil tussen aflopen en zoeken. */}
              {[...rij.registrations]
                .sort((a, b) => a.displayName.localeCompare(b.displayName, 'nl'))
                .map((r) => (
                  <tr key={r.userId} className="border-b border-brand-50 last:border-0">
                    <td className="py-2 pr-4 font-medium text-ink-900">{r.displayName}</td>
                    <td className="py-2 pr-4 text-ink-900/70">{r.email}</td>
                    <td className="py-2 pr-4 text-ink-900/70">
                      {[
                        r.lidnummerLvpoh && `LVPOH ${r.lidnummerLvpoh}`,
                        r.lidnummerSkj && `SKJ ${r.lidnummerSkj}`,
                      ]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </td>
                    <td className="py-2 text-ink-900/70">{r.note ?? ''}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AanmeldingenTab(): JSX.Element {
  const [rijen, setRijen] = useState<OverzichtRij[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    void apiGet<{ overzicht: OverzichtRij[] }>('/api/v1/agenda/aanmeldingen')
      .then((d) => setRijen(d.overzicht))
      .catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <div className="rounded-2xl border border-dashed border-brand-200 py-16 text-center">
        <p className="text-[14px] font-medium text-error">
          Kon de aanmeldingen niet laden. Ververs de pagina om het opnieuw te proberen.
        </p>
      </div>
    );
  }
  if (rijen === null) {
    return <div className="py-16 text-center text-[13px] text-ink-900/40">Laden…</div>;
  }

  // Komende evenementen eerst, daarna de afgelopen (omgekeerd chronologisch),
  // want daar gaat het bij navragen achteraf meestal om.
  const komend = rijen
    .filter((r) => isUpcoming(r.event.date))
    .sort((a, b) => a.event.date.localeCompare(b.event.date));
  const afgelopen = rijen
    .filter((r) => !isUpcoming(r.event.date))
    .sort((a, b) => b.event.date.localeCompare(a.event.date));

  if (rijen.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-brand-200 py-16 text-center">
        <p className="text-[14px] font-medium text-ink-900/40">Er zijn nog geen aanmeldingen.</p>
        <p className="mt-1 text-[12px] text-ink-900/35">
          Zet bij een evenement &quot;Aanmelden mogelijk&quot; aan om hier de deelnemers te zien.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {komend.length > 0 && (
        <div className="flex flex-col gap-4">
          {komend.map((rij) => (
            <OverzichtKaart key={rij.event.id} rij={rij} />
          ))}
        </div>
      )}
      {afgelopen.length > 0 && (
        <div>
          <h2 className="mb-3 text-[12px] font-semibold uppercase tracking-wider text-ink-900/35">
            Afgelopen
          </h2>
          <div className="flex flex-col gap-4">
            {afgelopen.map((rij) => (
              <OverzichtKaart key={rij.event.id} rij={rij} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Detail modal ────────────────────────────────────────────────────────────

function EventDetailModal({
  event,
  canManage,
  onEdit,
  onDelete,
  deleting,
  onClose,
  onAanmeldingGewijzigd,
}: {
  event: AgendaEvent;
  /** Beheer-knoppen tonen; de backend blijft de echte poort. */
  canManage: boolean;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
  onClose: () => void;
  onAanmeldingGewijzigd: () => void;
}): JSX.Element {
  const { account } = useTenantBrand();
  const { year, month, day, weekday } = parseDate(event.date);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [gedeeld, setGedeeld] = useState(false);

  // Delen gebeurt in het bericht zelf (titel, datum, locatie + link), niet via
  // het linkvoorbeeld: zo kiest de verzender wat er buiten de login komt.
  const regels = [event.title, `${langeDatum(event.date)} ${year}, ${event.time}`];
  if (event.location) regels.push(event.location);
  regels.push(`${window.location.origin}/agenda?item=${event.id}`);
  const text = regels.join('\n');

  async function deel(): Promise<void> {
    try {
      if (navigator.share) {
        await navigator.share({ text });
      } else {
        await navigator.clipboard.writeText(text);
        setGedeeld(true);
        setTimeout(() => setGedeeld(false), 2000);
      }
    } catch {
      // Gebruiker brak het deelvenster af; niets te melden.
    }
  }
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between gap-4 bg-brand-600 px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-white/70">
                {MONTH_ABBR[month]}
              </div>
              <div className="text-4xl font-black leading-none text-white">{day}</div>
            </div>
            <div className="h-12 w-px bg-white/20" />
            <div className="flex flex-col items-start gap-1.5">
              <TypeChip type={event.evenementtype} groep={event.intervisieGroupNaam} />
              <h2 className="text-[16px] font-bold leading-snug text-white">{event.title}</h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="mt-0.5 shrink-0 rounded-lg p-1 text-white/60 hover:bg-white/10 hover:text-white transition-colors"
          >
            <XIcon size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
          <div className="flex items-center gap-3 text-[13px] text-ink-900">
            <ClockIcon size={15} className="shrink-0 text-brand-600" />
            <span>
              {weekday} {day} {MONTH_SHORT[month]} {year}, {event.time}
            </span>
          </div>
          {(event.location || event.address) && (
            <div className="flex items-start gap-3 text-[13px] text-ink-900">
              <MapPinIcon size={15} className="mt-0.5 shrink-0 text-brand-600" />
              <a
                href={mapsUrl(event)}
                target="_blank"
                rel="noreferrer"
                className="underline decoration-brand-200 underline-offset-2 hover:text-brand-700"
              >
                {event.location && <span>{event.location}</span>}
                {event.location && event.address && <br />}
                {event.address && (
                  <span className={event.location ? 'text-ink-900/60' : undefined}>
                    {event.address}
                  </span>
                )}
              </a>
            </div>
          )}
          {event.organisatoren && event.organisatoren.length > 0 && (
            <div className="flex items-start gap-3 text-[13px] text-ink-900">
              <UsersIcon size={15} className="mt-0.5 shrink-0 text-brand-600" />
              <span>
                <span className="text-ink-900/60">Organisatie: </span>
                {event.organisatoren.map((o) => o.displayName).join(', ')}
              </span>
            </div>
          )}
          {event.sprekers && event.sprekers.length > 0 && (
            <div className="flex items-start gap-3 text-[13px] text-ink-900">
              <MicIcon size={15} className="mt-0.5 shrink-0 text-brand-600" />
              <span>
                <span className="text-ink-900/60">
                  {event.sprekers.length === 1 ? 'Spreker: ' : 'Sprekers: '}
                </span>
                {event.sprekers.map((s, i) => (
                  <span key={`${s.userId ?? 'extern'}-${s.naam}`}>
                    {i > 0 && ', '}
                    {s.userId ? (
                      <Link
                        to={`/smoelenboek?persoon=${s.userId}`}
                        className="underline decoration-brand-200 underline-offset-2 hover:text-brand-700"
                      >
                        {s.naam}
                      </Link>
                    ) : (
                      <>
                        {s.naam} <span className="text-ink-900/50">(extern)</span>
                      </>
                    )}
                  </span>
                ))}
              </span>
            </div>
          )}
          {event.teamsUrl && (
            <div className="flex flex-col gap-1.5">
              <a
                href={event.teamsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-fit items-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-[13px] font-semibold text-white hover:bg-brand-700 transition-colors"
              >
                <VideoIcon size={15} />
                Deelnemen via Teams
              </a>
              <p className="text-[11px] text-ink-900/45">{teamsHulptekst(account)}</p>
            </div>
          )}
          {event.teamsUrlNaAanmelding && (
            <div className="flex items-center gap-3 text-[13px] text-ink-900/70">
              <VideoIcon size={15} className="shrink-0 text-brand-600" />
              <span>Online via Teams. Meld je aan om de link te zien.</span>
            </div>
          )}
          {event.description && (
            <RichText
              value={event.description}
              className="text-[13px] leading-relaxed text-ink-900/70"
            />
          )}

          {event.registrationsOpen && (
            <AgendaRegistrations event={event} onGewijzigd={onAanmeldingGewijzigd} />
          )}
        </div>

        <div className="flex shrink-0 flex-wrap gap-2 border-t border-brand-100 px-6 py-4">
          <button
            type="button"
            onClick={() => downloadIcs(event)}
            className="flex items-center gap-1.5 rounded-xl border border-brand-200 px-4 py-2 text-[13px] font-medium text-brand-700 hover:bg-brand-50 transition-colors"
          >
            <CalendarPlusIcon size={13} />
            In agenda
          </button>
          <button
            type="button"
            onClick={() => void deel()}
            className="flex items-center gap-1.5 rounded-xl border border-brand-200 px-4 py-2 text-[13px] font-medium text-brand-700 hover:bg-brand-50 transition-colors"
          >
            <Share2Icon size={13} />
            {gedeeld ? 'Gekopieerd' : 'Delen'}
          </button>
          {/* Het deelmenu van Windows toont WhatsApp niet; wa.me opent op de pc
              de WhatsApp-app of WhatsApp Web, op de telefoon de app (Linda). */}
          <a
            href={`https://wa.me/?text=${encodeURIComponent(text)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 rounded-xl border border-brand-200 px-4 py-2 text-[13px] font-medium text-brand-700 hover:bg-brand-50 transition-colors"
          >
            <MessageCircleIcon size={13} />
            WhatsApp
          </a>
          {canManage && (
            <>
              <button
                type="button"
                onClick={onEdit}
                className="flex items-center gap-1.5 rounded-xl border border-brand-200 px-4 py-2 text-[13px] font-medium text-brand-700 hover:bg-brand-50 transition-colors"
              >
                <PencilIcon size={13} />
                Aanpassen
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!confirmDelete) {
                    setConfirmDelete(true);
                    return;
                  }
                  onDelete();
                }}
                onBlur={() => setConfirmDelete(false)}
                disabled={deleting}
                className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-[13px] font-medium text-error hover:bg-error/10 transition-colors disabled:opacity-50"
              >
                <Trash2Icon size={13} />
                {confirmDelete ? 'Zeker verwijderen?' : 'Verwijderen'}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded-xl bg-brand-50 px-4 py-2 text-[13px] font-medium text-brand-700 hover:bg-brand-100 transition-colors"
          >
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Event card ──────────────────────────────────────────────────────────────

function EventCard({ event, onClick }: { event: AgendaEvent; onClick: () => void }): JSX.Element {
  const { day, month, weekday } = parseDate(event.date);
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full overflow-hidden rounded-2xl border border-brand-100 bg-white shadow-sm text-left transition-shadow hover:shadow-md"
    >
      <div className="flex w-20 shrink-0 flex-col items-center justify-center bg-brand-600 py-4">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-white/70">
          {MONTH_ABBR[month]}
        </span>
        <span className="text-3xl font-black leading-none text-white">{day}</span>
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1.5 px-4 py-3">
        <TypeChip
          type={event.evenementtype}
          groep={event.intervisieGroupNaam}
          className="self-start"
        />
        <h2 className="line-clamp-2 text-[14px] font-bold leading-snug text-ink-900">
          {event.title}
        </h2>
        <div className="flex items-center gap-1.5 text-[12px] text-ink-900/55">
          <ClockIcon size={11} className="shrink-0" />
          <span>
            {weekday} {day} {MONTH_SHORT[month]}, {event.time}
          </span>
        </div>
        {event.location && (
          <div className="flex items-center gap-1.5 text-[12px] text-ink-900/55">
            <MapPinIcon size={11} className="shrink-0" />
            <span className="truncate">{event.location}</span>
          </div>
        )}
        {/* Geen link-in-een-knop: de kaart ís een knop, dus hier alleen de
            aankondiging. De deelnameknop staat in het evenement zelf. */}
        {(event.teamsUrl || event.teamsUrlNaAanmelding) && (
          <div className="flex items-center gap-1.5 text-[12px] font-medium text-brand-700">
            <VideoIcon size={11} className="shrink-0" />
            <span>Online via Teams</span>
          </div>
        )}
      </div>
    </button>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export function AgendaPage(): JSX.Element {
  const { user } = useAuth();
  const [events, setEvents] = useState<AgendaEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // Het geopende item staat in de URL (`?item=<id>`) zodat de link deelbaar is
  // en direct het juiste item opent.
  const [params, setParams] = useSearchParams();
  const selected = events.find((e) => e.id === params.get('item')) ?? null;
  const setSelected = (event: AgendaEvent | null): void =>
    setParams(event ? { item: event.id } : {}, { replace: true });
  const [editing, setEditing] = useState<AgendaEvent | null | 'new'>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [tab, setTab] = useState<'agenda' | 'aanmeldingen'>('agenda');
  // Beheerder ziet standaard alleen intervisiesessies van zijn eigen groepen;
  // met deze schakelaar ook die van andere groepen, om ze te kunnen bewerken.
  const [alleIntervisie, setAlleIntervisie] = useState(false);

  // Beheer-poort (besluit 30 juli 2026): kantoorpersoneel of beheerder. Dit
  // verbergt alleen knoppen; de backend weigert de mutatie sowieso.
  const isAdmin = user?.role === 'suppohrt_admin';
  // RHOGO-beheerders beheren de agenda van hun eigen tenant (ADR 0018).
  const canManage = isAdmin || user?.role === 'zorggroep_admin' || user?.isHeadOffice === true;
  // Een organisator (#220) krijgt het tabblad Aanmeldingen voor de eigen items.
  const isOrganisator = events.some((e) => e.organisatoren?.some((o) => o.userId === user?.id));
  const canSeeAanmeldingen = canManage || isOrganisator;

  const laadEvents = useCallback((): Promise<void> => {
    return apiGet<{ events: AgendaEvent[] }>(
      alleIntervisie ? '/api/v1/agenda?alles=1' : '/api/v1/agenda',
    )
      .then((d) => setEvents(d.events))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [alleIntervisie]);

  useEffect(() => {
    void laadEvents();
  }, [laadEvents]);

  async function handleSave(input: CreateAgendaEventInput): Promise<void> {
    setSaving(true);
    setSaveError('');
    try {
      if (editing && editing !== 'new') {
        const { event } = await apiPut<{ event: AgendaEvent }>(
          `/api/v1/agenda/${editing.id}`,
          input as UpdateAgendaEventInput,
        );
        setEvents((prev) => prev.map((e) => (e.id === event.id ? event : e)));
        setSelected(event);
      } else {
        const { event } = await apiPost<{ event: AgendaEvent }>('/api/v1/agenda', input);
        setEvents((prev) => [...prev, event]);
        setSelected(event);
      }
      setEditing(null);
    } catch (err) {
      // De backend weigert bijvoorbeeld een intervisiesessie van niet-beheerders
      // of een onbekende groep; die uitleg hoort in het formulier te landen.
      setSaveError(
        err instanceof ApiError ? err.message : 'Opslaan lukte niet. Probeer het opnieuw.',
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string): Promise<void> {
    setDeleting(true);
    try {
      await apiDelete(`/api/v1/agenda/${id}`);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      setSelected(null);
    } finally {
      setDeleting(false);
    }
  }

  function openEdit(event: AgendaEvent): void {
    setSaveError('');
    setSelected(null);
    setEditing(event);
  }

  const upcoming = events
    .filter((e) => isUpcoming(e.date))
    .sort((a, b) => a.date.localeCompare(b.date));

  const past = events
    .filter((e) => !isUpcoming(e.date))
    .sort((a, b) => b.date.localeCompare(a.date));

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-5 py-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-[22px] font-bold text-ink-900">Agenda</h1>
          {isAdmin && tab === 'agenda' && (
            <label className="ml-auto mr-3 flex items-center gap-1.5 text-[12px] text-ink-900/60">
              <input
                type="checkbox"
                checked={alleIntervisie}
                onChange={(e) => setAlleIntervisie(e.target.checked)}
                className="h-3.5 w-3.5 accent-brand-600"
              />
              Alle intervisiesessies (beheer)
            </label>
          )}
          {canManage && tab === 'agenda' && (
            <button
              type="button"
              onClick={() => {
                setSaveError('');
                setEditing('new');
              }}
              className="flex items-center gap-1.5 rounded-lg border border-brand-100 bg-white px-3 py-1.5 text-[13px] font-medium text-ink-900/70 shadow-sm hover:bg-brand-50 transition-colors"
            >
              <PlusIcon size={14} />
              Toevoegen
            </button>
          )}
        </div>

        {/* Alleen kantoorpersoneel en beheerders houden aanmeldingen bij; voor de
            rest is er niets te kiezen en blijft de pagina zoals hij was. */}
        {canSeeAanmeldingen && (
          <div className="mb-6 flex gap-1 border-b border-brand-100">
            {(
              [
                ['agenda', 'Agenda'],
                ['aanmeldingen', 'Aanmeldingen'],
              ] as const
            ).map(([waarde, label]) => (
              <button
                key={waarde}
                type="button"
                aria-pressed={tab === waarde}
                onClick={() => setTab(waarde)}
                className={
                  tab === waarde
                    ? '-mb-px border-b-2 border-brand-600 px-4 py-2 text-[13px] font-semibold text-brand-700'
                    : '-mb-px border-b-2 border-transparent px-4 py-2 text-[13px] font-medium text-ink-900/55 hover:text-ink-900'
                }
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {canSeeAanmeldingen && tab === 'aanmeldingen' ? (
          <AanmeldingenTab />
        ) : loading ? (
          <div className="py-16 text-center text-[13px] text-ink-900/40">Laden…</div>
        ) : loadError ? (
          <div className="rounded-2xl border border-dashed border-brand-200 py-16 text-center">
            <p className="text-[14px] font-medium text-error">
              Kon de agenda niet laden. Ververs de pagina om het opnieuw te proberen.
            </p>
          </div>
        ) : (
          <>
            {upcoming.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-brand-200 py-16 text-center">
                <p className="text-[14px] font-medium text-ink-900/40">
                  Geen aankomende evenementen.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {upcoming.map((event) => (
                  <EventCard key={event.id} event={event} onClick={() => setSelected(event)} />
                ))}
              </div>
            )}

            {past.length > 0 && (
              <div className="mt-10">
                <h2 className="mb-3 text-[12px] font-semibold uppercase tracking-wider text-ink-900/35">
                  Afgelopen
                </h2>
                <div className="grid grid-cols-1 gap-4 opacity-50 sm:grid-cols-2">
                  {past.map((event) => (
                    <EventCard key={event.id} event={event} onClick={() => setSelected(event)} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {selected !== null && (
        <EventDetailModal
          event={selected}
          // Intervisiesessies zijn strikt beheerder-werk; kantoorpersoneel ziet
          // daar geen bewerk-knoppen (de backend geeft anders een 403).
          canManage={canManage && (selected.evenementtype !== 'intervisie' || isAdmin)}
          onEdit={() => openEdit(selected)}
          onDelete={() => void handleDelete(selected.id)}
          deleting={deleting}
          onClose={() => setSelected(null)}
          // De Teams-link hangt af van je aanmelding en komt van de backend,
          // dus na aan- of afmelden de lijst opnieuw ophalen.
          onAanmeldingGewijzigd={() => void laadEvents()}
        />
      )}

      {editing !== null && (
        <EventFormModal
          initial={editing === 'new' ? null : editing}
          isAdmin={isAdmin}
          onSave={(input) => void handleSave(input)}
          onClose={() => {
            setSaveError('');
            setEditing(null);
          }}
          saving={saving}
          saveError={saveError}
        />
      )}
    </Layout>
  );
}
